import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { API } from "../../constants/API";

export interface InventoryStockReportItem {
  _id: string;
  ISBN: string;
  FORMAT: string;
  TITLE: string;
  AUTHOR: string;
  SEASON: string;
  INDY_USABLE: number;
  BACKORDER_QTY: number;
  US_PRICE: number;
  PUB_DATE: string;
  DIVISION: string;
  IMPRINT: string;
  DISTRIBUTION_CLIENT: string;
  CATEGORY1: string;
  BISAC_1: string;
  BISAC1_DESC: string;
  MANAGING_EDITOR: string;
  EDITOR: string;
  BISAC_STATUS: string;
  LICENSOR: string;
  SERIES_NAME: string;
}

interface SortObj {
  heading: string;
  sortType: string;
}

interface InventoryStockReportState {
  data: InventoryStockReportItem[];
  status: string;
  hasMore: boolean;
  statusCode: number;
  error: string | null;
  isLoading: boolean;
  page: number;
  limit: number;
  selectedRowIndex: number | null;
  sortBy: SortObj;
  // Filter options
  isbns: any[];
  authors: any[];
  seasons: any[];
  divisions: any[];
  imprints: any[];
  formats: any[];
  managingEditors: any[];
  editors: any[];
  bisacStatus: any[];
  licensors: any[];
  series: any[];
  distributionClients: any[];
  // Pagination for filters
  isbnPage: number;
  authorPage: number;
  isbnLimit: number;
  authorLimit: number;
  isbnHasMore: boolean;
  authorHasMore: boolean;
  isbnString: string;
  authorString: string;
}

const initialState: InventoryStockReportState = {
  data: [],
  page: 1,
  limit: 25,
  status: "",
  hasMore: false,
  error: null,
  statusCode: 0,
  isLoading: false,
  selectedRowIndex: null,
  sortBy: {
    heading: "ISBN",
    sortType: "asc",
  },
  isbns: [],
  authors: [],
  seasons: [],
  divisions: [],
  imprints: [],
  formats: [],
  managingEditors: [],
  editors: [],
  bisacStatus: [],
  licensors: [],
  series: [],
  distributionClients: [],
  isbnPage: 1,
  authorPage: 1,
  isbnLimit: 10,
  authorLimit: 10,
  isbnHasMore: false,
  authorHasMore: false,
  isbnString: "",
  authorString: "",
};

export const FetchInventoryStockReport = createAsyncThunk(
  "FetchInventoryStockReport",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await axios.post(API.FETCH_INVENTORY_STOCK_REPORT, data);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

export const FetchInventorySeasons = createAsyncThunk(
  "FetchInventorySeasons",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await axios.post(API.FETCH_INVENTORY_SEASONS, data);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

export const FetchInventoryDivision = createAsyncThunk(
  "FetchInventoryDivision",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await axios.post(API.FETCH_INVENTORY_DIVISION, data);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

export const FetchInventoryImprints = createAsyncThunk(
  "FetchInventoryImprints",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await axios.post(API.FETCH_INVENTORY_IMPRINTS, data);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

export const FetchInventoryFormat = createAsyncThunk(
  "FetchInventoryFormat",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await axios.post(API.FETCH_INVENTORY_FORMAT, data);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

export const FetchInventoryManagingEditor = createAsyncThunk(
  "FetchInventoryManagingEditor",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await axios.post(API.FETCH_INVENTORY_MANAGING_EDITOR, data);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

export const FetchInventoryEditor = createAsyncThunk(
  "FetchInventoryEditor",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await axios.post(API.FETCH_INVENTORY_EDITOR, data);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

export const FetchInventoryBisacStatus = createAsyncThunk(
  "FetchInventoryBisacStatus",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await axios.post(API.FETCH_INVENTORY_BISAC_STATUS, data);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

export const FetchInventoryAuthor = createAsyncThunk(
  "FetchInventoryAuthor",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await axios.post(API.FETCH_INVENTORY_AUTHOR, data);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

export const FetchInventoryIsbns = createAsyncThunk(
  "FetchInventoryIsbns",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await axios.post(API.FETCH_INVENTORY_ISBNS, data);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

export const FetchInventoryLicensor = createAsyncThunk(
  "FetchInventoryLicensor",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await axios.post(API.FETCH_INVENTORY_LICENSOR, data);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

export const FetchInventorySeries = createAsyncThunk(
  "FetchInventorySeries",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await axios.post(API.FETCH_INVENTORY_SERIES, data);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

export const FetchInventoryDistributionClient = createAsyncThunk(
  "FetchInventoryDistributionClient",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await axios.post(API.FETCH_INVENTORY_DISTRIBUTION_CLIENT, data);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

const InventoryStockReportSlice = createSlice({
  name: "InventoryStockReport",
  initialState,
  reducers: {
    emptyInventoryStockReportPage: (state) => {
      state.page = initialState.page;
      state.data = initialState.data;
      state.selectedRowIndex = initialState.selectedRowIndex;
      state.status = initialState.status;
      state.statusCode = initialState.statusCode;
      state.error = initialState.error;
      state.isLoading = initialState.isLoading;
      state.limit = initialState.limit;
      state.hasMore = initialState.hasMore;
    },
    UpdateInventoryStockReportPagination: (state) => {
      state.page = state.page + 1;
    },
    resetInventoryStockReportPage: (state) => {
      state.page = initialState.page;
    },
    setSelectedRowIndex: (state, action) => {
      state.selectedRowIndex = action.payload;
    },
    inventoryStockReportSortBy: (state, action) => {
      state.sortBy = action.payload;
    },
    addIsbnPage: (state) => {
      if (state.isbnHasMore) state.isbnPage += 1;
    },
    resetIsbnPage: (state) => {
      state.isbnPage = initialState.isbnPage;
      state.isbnHasMore = true;
    },
    setIsbnString: (state, action) => {
      state.isbnString = action.payload;
    },
    addAuthorPage: (state) => {
      if (state.authorHasMore) state.authorPage += 1;
    },
    resetAuthorPage: (state) => {
      state.authorPage = initialState.authorPage;
      state.authorHasMore = true;
    },
    setAuthorString: (state, action) => {
      state.authorString = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(FetchInventoryStockReport.pending, (state) => {
        state.isLoading = true;
        state.status = "Loading";
      })
      .addCase(FetchInventoryStockReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.status = "success";
        state.hasMore = action.payload.hasMore;
        // Use the requested page from the thunk args instead of the current state.page.
        // This prevents older, in‑flight requests (e.g. page > 1 with TitleListIds)
        // from overwriting newer first‑page results when they resolve later.
        const requestedPage = action.meta?.arg?.page ?? 1;

        if (action.payload.filteredNew || requestedPage === 1) {
          // New filter set or an explicit first‑page fetch – replace data.
          state.data = action.payload.TitleLists;
          state.selectedRowIndex = 0;
        } else {
          // Subsequent pages for the same filter set – append.
          state.data.push(...action.payload.TitleLists);
        }
      })
      .addCase(FetchInventoryStockReport.rejected, (state) => {
        state.isLoading = false;
        state.status = "Failed";
        state.error = "Error";
      })
      // Filter APIs
      .addCase(FetchInventorySeasons.pending, (state) => { state.isLoading = true; })
      .addCase(FetchInventorySeasons.fulfilled, (state, action) => {
        const payload = action.payload?.data || action.payload || [];
        state.seasons = Array.isArray(payload) ? payload : [];
        state.isLoading = false;
      })
      .addCase(FetchInventorySeasons.rejected, (state) => { state.isLoading = false; })
      
      .addCase(FetchInventoryDivision.pending, (state) => { state.isLoading = true; })
      .addCase(FetchInventoryDivision.fulfilled, (state, action) => {
        const payload = action.payload?.data || action.payload || [];
        state.divisions = Array.isArray(payload) ? payload : [];
        state.isLoading = false;
      })
      .addCase(FetchInventoryDivision.rejected, (state) => { state.isLoading = false; })
      
      .addCase(FetchInventoryImprints.pending, (state) => { state.isLoading = true; })
      .addCase(FetchInventoryImprints.fulfilled, (state, action) => {
        const payload = action.payload?.data || action.payload || [];
        state.imprints = Array.isArray(payload) ? payload : [];
        state.isLoading = false;
      })
      .addCase(FetchInventoryImprints.rejected, (state) => { state.isLoading = false; })
      
      .addCase(FetchInventoryFormat.pending, (state) => { state.isLoading = true; })
      .addCase(FetchInventoryFormat.fulfilled, (state, action) => {
        const payload = action.payload?.data || action.payload || [];
        state.formats = Array.isArray(payload) ? payload : [];
        state.isLoading = false;
      })
      .addCase(FetchInventoryFormat.rejected, (state) => { state.isLoading = false; })
      
      .addCase(FetchInventoryManagingEditor.pending, (state) => { state.isLoading = true; })
      .addCase(FetchInventoryManagingEditor.fulfilled, (state, action) => {
        const payload = action.payload?.data || action.payload || [];
        state.managingEditors = Array.isArray(payload) ? payload : [];
        state.isLoading = false;
      })
      .addCase(FetchInventoryManagingEditor.rejected, (state) => { state.isLoading = false; })
      
      .addCase(FetchInventoryEditor.pending, (state) => { state.isLoading = true; })
      .addCase(FetchInventoryEditor.fulfilled, (state, action) => {
        const payload = action.payload?.data || action.payload || [];
        state.editors = Array.isArray(payload) ? payload : [];
        state.isLoading = false;
      })
      .addCase(FetchInventoryEditor.rejected, (state) => { state.isLoading = false; })
      
      .addCase(FetchInventoryBisacStatus.pending, (state) => { state.isLoading = true; })
      .addCase(FetchInventoryBisacStatus.fulfilled, (state, action) => {
        const payload = action.payload?.data || action.payload || [];
        state.bisacStatus = Array.isArray(payload) ? payload : [];
        state.isLoading = false;
      })
      .addCase(FetchInventoryBisacStatus.rejected, (state) => { state.isLoading = false; })
      
      .addCase(FetchInventoryAuthor.pending, (state) => { state.isLoading = true; })
      .addCase(FetchInventoryAuthor.fulfilled, (state, action) => {
        const payload = action.payload?.data || action.payload || [];
        const hasMore = action.payload?.hasMore || false;
        const filteredNew = action.payload?.filteredNew;
        
        if (filteredNew || state.authorPage === 1) {
          state.authors = Array.isArray(payload) ? payload : [];
          state.authorPage = 1;
        } else {
          const existingIds = new Set(state.authors.map((item: any) => item._id || item.value));
          const newItems = (Array.isArray(payload) ? payload : []).filter(
            (item: any) => !existingIds.has(item._id || item.value)
          );
          state.authors.push(...newItems);
        }
        state.authorHasMore = hasMore;
        state.isLoading = false;
      })
      .addCase(FetchInventoryAuthor.rejected, (state) => { state.isLoading = false; })
      
      .addCase(FetchInventoryIsbns.pending, (state) => { state.isLoading = true; })
      .addCase(FetchInventoryIsbns.fulfilled, (state, action) => {
        const payload = action.payload?.data || action.payload || [];
        const hasMore = action.payload?.hasMore || false;
        const filteredNew = action.payload?.filteredNew;
        
        if (filteredNew || state.isbnPage === 1) {
          state.isbns = Array.isArray(payload) ? payload : [];
          state.isbnPage = 1;
        } else {
          const existingIds = new Set(state.isbns.map((item: any) => item._id || item.ISBN || item.value));
          const newItems = (Array.isArray(payload) ? payload : []).filter(
            (item: any) => !existingIds.has(item._id || item.ISBN || item.value)
          );
          state.isbns.push(...newItems);
        }
        state.isbnHasMore = hasMore;
        state.isLoading = false;
      })
      .addCase(FetchInventoryIsbns.rejected, (state) => { state.isLoading = false; })
      
      .addCase(FetchInventoryLicensor.pending, (state) => { state.isLoading = true; })
      .addCase(FetchInventoryLicensor.fulfilled, (state, action) => {
        const payload = action.payload?.data || action.payload || [];
        state.licensors = Array.isArray(payload) ? payload : [];
        state.isLoading = false;
      })
      .addCase(FetchInventoryLicensor.rejected, (state) => { state.isLoading = false; })
      
      .addCase(FetchInventorySeries.pending, (state) => { state.isLoading = true; })
      .addCase(FetchInventorySeries.fulfilled, (state, action) => {
        const payload = action.payload?.data || action.payload || [];
        state.series = Array.isArray(payload) ? payload : [];
        state.isLoading = false;
      })
      .addCase(FetchInventorySeries.rejected, (state) => { state.isLoading = false; })
      
      .addCase(FetchInventoryDistributionClient.pending, (state) => { state.isLoading = true; })
      .addCase(FetchInventoryDistributionClient.fulfilled, (state, action) => {
        const payload = action.payload?.data || action.payload || [];
        state.distributionClients = Array.isArray(payload) ? payload : [];
        state.isLoading = false;
      })
      .addCase(FetchInventoryDistributionClient.rejected, (state) => { state.isLoading = false; });
  },
});


export const {
  emptyInventoryStockReportPage,
  UpdateInventoryStockReportPagination,
  resetInventoryStockReportPage,
  setSelectedRowIndex,
  inventoryStockReportSortBy,
  addIsbnPage,
  resetIsbnPage,
  setIsbnString,
  addAuthorPage,
  resetAuthorPage,
  setAuthorString,
} = InventoryStockReportSlice.actions;
export default InventoryStockReportSlice.reducer;
